import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import RttRoundedIcon from '@mui/icons-material/RttRounded';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import CardMark from '../../../../../../scout-new/components/message-card/CardMark';
import CommonIconButton from '../../../../../../components/common-icon-button/CommonIconButton';
import { ICONS } from '../../../../../../icons';
import { setEditableMessage, setIsDirty } from '../../../../../../redux/activ8/activ8MessagesReducer';
import EditSection from './EditSection';
import { useProject } from '../../../../../../context/ProjectContext';

import scss from './MessageCard.module.scss';

function MessageCard({
  bindNextAction,
  message: messageData,
  onGoToStory,
  goToStoryTooltip,
  onSave,
  onAdjust,
  onDelete,
  onInfo,
  isShowSaveButton,
}) {
  const { id, count, message, creator, newMessage, savedId } = messageData;

  const { t } = useTranslation('activ8');
  const dispatch = useDispatch();
  const { actions } = useProject();

  const isButtonsDisabled = false;
  const editableMessage = useSelector((state) => state.activ8Messages.editableMessage);

  const isSaved = !!savedId;

  const [messageLocalText, setMessageLocalText] = useState(message);

  const handleDelete = () => {
    onDelete(messageData);
  };

  const handleInfo = () => {
    onInfo(messageData);
  };

  const handleSave = () => {
    onSave(messageData);
  };

  const handleEditStart = () => {
    bindNextAction(() => dispatch(setEditableMessage({ id, message: message, savedId })));
  };

  const handleSaveTextChanges = ({ newText }) => {
    actions('editMessage', { id, message: newText, savedId });
  };

  const handleGoToStory = () => {
    onGoToStory(messageData);
  };

  const handleAdjust = () => {
    onAdjust(messageData);
  };

  useEffect(() => {
    dispatch(setIsDirty(messageLocalText !== message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, messageLocalText]);

  useEffect(() => {
    setMessageLocalText(message);
  }, [message]);

  return (
    <div className={scss.cardWrapper}>
      <div className={scss.leftContent}>
        <div className={scss.idCell}>{count}</div>

        <div className={scss.contentWrapper}>
          {editableMessage.id === id ? (
            <EditSection
              setMessageLocalText={setMessageLocalText}
              message={message}
              messageLocalText={messageLocalText}
              onSave={handleSaveTextChanges}
            />
          ) : (
            <div className={scss.textCell}>{message}</div>
          )}
        </div>
      </div>

      <div className={scss.actionsCell}>
        {onSave && isShowSaveButton ? (
          <CommonIconButton
            icon={SaveOutlinedIcon}
            onClick={handleSave}
            disabled={isButtonsDisabled}
            iconClassName={clsx(scss.saveIcon, isSaved && scss.fillIconSelected)}
            tooltip={isSaved ? t('saved') : t('save')}
          />
        ) : null}
        {onInfo && isSaved ? (
          <CommonIconButton
            icon={ICONS.Info}
            onClick={handleInfo}
            disabled={isButtonsDisabled}
            isStrokeIcon
            tooltip={t('showInfo')}
          />
        ) : null}
        {isSaved ? (
          <CommonIconButton
            icon={ICONS.Edit}
            onClick={handleEditStart}
            disabled={isButtonsDisabled}
            tooltip={t('edit')}
          />
        ) : null}
        {onAdjust ? (
          <CommonIconButton
            icon={BuildOutlinedIcon}
            onClick={handleAdjust}
            // disabled={isButtonsDisabled}
            disabled={true}
            tooltip={t('goToAdjust')}
          />
        ) : null}
        {onGoToStory ? (
          <CommonIconButton
            icon={RttRoundedIcon}
            onClick={handleGoToStory}
            disabled={isButtonsDisabled}
            tooltip={goToStoryTooltip ?? t('goToStory')}
          />
        ) : null}
        {onDelete ? (
          <CommonIconButton
            icon={ICONS.Delete}
            onClick={handleDelete}
            disabled={isButtonsDisabled}
            tooltip={t('delete')}
          />
        ) : null}
      </div>

      <CardMark isNew={newMessage} isManual={creator === 'MANUAL'} />
    </div>
  );
}

export default MessageCard;
